import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { WidgetCatalogResponse, WidgetEntry, WidgetInstance } from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import { WidgetPreview } from './WidgetPreview';
import './WidgetPicker.css';

interface Props {
  onSelect(instance: WidgetInstance): void;
  onClose(): void;
}

/**
 * Modal that lets the operator add a widget to the current dashboard.
 * Fetches the server-hosted catalog (`GET /api/widgets/catalog`) once per
 * open, then renders each entry as a card. Selecting a card mints a fresh
 * <see cref="WidgetInstance"/> — a new UUID + a config object seeded from the
 * entry's JSON-Schema defaults — and hands it to the caller.
 *
 * The picker deliberately does not know about Gridstack or any layout host.
 * It emits a portable instance; whatever the DashboardPage does with it
 * (place, position, render) is the DashboardPage's business.
 */
export function WidgetPicker({ onSelect, onClose }: Props) {
  const state = useAsync(
    (signal) => api.get<WidgetCatalogResponse>('/api/widgets/catalog', signal),
    [],
  );
  const [filter, setFilter] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close on Escape — a minimum a11y expectation for a modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Focus the filter input on open so the user can type-to-search immediately.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (state.status !== 'ok') return [] as WidgetEntry[];
    const q = filter.trim().toLowerCase();
    if (!q) return state.data.widgets;
    return state.data.widgets.filter((w) =>
      w.displayName.toLowerCase().includes(q)
        || w.description.toLowerCase().includes(q)
        || w.id.toLowerCase().includes(q)
        || w.compatibleCollectionKinds.some((k) => k.toLowerCase().includes(q)),
    );
  }, [state, filter]);

  const pick = (entry: WidgetEntry) => {
    onSelect({
      id: crypto.randomUUID(),
      widgetTypeId: entry.id,
      config: extractDefaults(entry.configSchema),
    });
  };

  // Enter picks the single filtered match — makes keyboard-only flow fast.
  const onFilterKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && filtered.length === 1) {
      e.preventDefault();
      pick(filtered[0]);
    }
  };

  return (
    <div class="hv-widget-picker" role="dialog" aria-modal="true" aria-labelledby="hv-widget-picker-title">
      <div class="hv-widget-picker__backdrop" onClick={onClose} />
      <div class="hv-widget-picker__panel">
        <header class="hv-widget-picker__header">
          <h2 id="hv-widget-picker-title" class="hv-widget-picker__title">Widget hinzufügen</h2>
          <button
            type="button"
            class="hv-widget-picker__close"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
          >
            ×
          </button>
        </header>
        <div class="hv-widget-picker__body">
          {state.status === 'loading' && <p class="muted">Katalog wird geladen…</p>}
          {state.status === 'error' && (
            <p class="hv-widget-picker__error">Widget-Katalog konnte nicht geladen werden.</p>
          )}
          {state.status === 'ok' && state.data.widgets.length === 0 && (
            <p class="muted">Kein Widget-Typ verfügbar.</p>
          )}
          {state.status === 'ok' && state.data.widgets.length > 0 && (
            <>
              <div class="hv-widget-picker__filter-row">
                <input
                  ref={inputRef}
                  type="search"
                  class="hv-widget-picker__filter"
                  placeholder="Filtern (Name, Beschreibung, Collection-Kind)…"
                  value={filter}
                  onInput={(e) => setFilter((e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={onFilterKey}
                  aria-label="Widgets filtern"
                />
                <span class="hv-widget-picker__count muted">
                  {filter ? `${filtered.length} / ${state.data.widgets.length}` : `${state.data.widgets.length} Typen`}
                </span>
              </div>
              {filtered.length === 0 ? (
                <p class="muted">Keine Widgets für „{filter}".</p>
              ) : (
                <div class="hv-widget-picker__grid" role="list">
                  {filtered.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      class="hv-widget-card"
                      onClick={() => pick(w)}
                      role="listitem"
                      title={w.description}
                    >
                      <WidgetPreview widgetTypeId={w.id} />
                      <div class="hv-widget-card__title">{w.displayName}</div>
                      <div class="hv-widget-card__desc">{w.description}</div>
                      {w.compatibleCollectionKinds.length > 0 && (
                        <div class="hv-widget-card__meta">
                          {w.compatibleCollectionKinds.map((k) => (
                            <span key={k} class="hv-widget-card__badge">{k}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Walk a JSON-Schema-Draft-07 shape and build a starter config from
 * `properties[*].default` where present. Missing defaults land as absent
 * keys — the config-editor (later step) or the widget's render function
 * fills them from its own defaults. Nested objects are traversed once; we
 * do not attempt to synthesize objects when only their children have
 * defaults, because that is a policy that belongs in the config-editor.
 */
function extractDefaults(schema: unknown): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  if (!schema || typeof schema !== 'object') return config;
  const s = schema as { properties?: Record<string, { default?: unknown }> };
  if (!s.properties) return config;
  for (const [key, prop] of Object.entries(s.properties)) {
    if (prop && typeof prop === 'object' && 'default' in prop) {
      config[key] = prop.default;
    }
  }
  return config;
}
